import { useState } from "react";
import { Printer, Download, X, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const ALL_LEVELS = ["2.5", "3.0", "3.5", "4.0", "4.5"];
const LEVEL_LABELS = {
  "2.5": "Beginner",
  "3.0": "Recreational",
  "3.5": "Intermediate",
  "4.0": "Strong Intermediate",
  "4.5": "Advanced",
};

/**
 * Coach picks which DUPR levels they want printed (any combo of 2.5 → 4.5).
 * Two outputs:
 *   - "Open print view"  — fresh tab with a print-styled HTML page that auto-opens the print dialog
 *   - "Download PDF"     — true PDF via the backend's reportlab endpoint
 */
export default function PrintChecklistModal({ open, onClose, defaultLevels = [], studentName = "", studentId = "" }) {
  const [picked, setPicked] = useState(new Set(defaultLevels.length ? defaultLevels : ["2.5"]));
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const toggle = (lv) => {
    setPicked((cur) => {
      const next = new Set(cur);
      if (next.has(lv)) next.delete(lv);
      else next.add(lv);
      return next;
    });
  };
  const all = () => setPicked(new Set(ALL_LEVELS));
  const none = () => setPicked(new Set());

  const params = () => {
    const lv = ALL_LEVELS.filter((l) => picked.has(l)).join(",");
    const sn = studentName ? `&student_name=${encodeURIComponent(studentName)}` : "";
    const pid = studentId ? `&player_id=${encodeURIComponent(studentId)}` : "";
    return { lv, sn, pid };
  };

  const openHtml = async () => {
    const { lv, sn, pid } = params();
    if (!lv) { toast.error("Pick at least one level"); return; }
    // iOS Safari blocks window.open() after any await — we must open the tab
    // synchronously inside the click handler while the user-gesture is still
    // "hot", then write into it once the fetch resolves. If the tab is nulled
    // it's a genuine pop-up-blocker denial; only then do we surface the toast.
    const w = window.open("about:blank", "_blank");
    if (!w) {
      toast.error("Pop-up blocked — allow pop-ups for this site, then tap Open again.");
      return;
    }
    try {
      w.document.open();
      w.document.write(
        '<!doctype html><meta charset="utf-8"><title>Preparing checklist…</title>' +
        '<style>body{font-family:-apple-system,Segoe UI,sans-serif;color:#111;padding:24px;text-align:center}</style>' +
        '<p>Preparing your checklist…</p>'
      );
      w.document.close();
    } catch { /* ignore — some browsers throw if the tab is still initialising */ }
    setBusy(true);
    try {
      const { data } = await api.get(`/assessment-checklist/html?levels=${lv}${sn}${pid}&autoprint=1`, {
        responseType: "text",
        transformResponse: (x) => x,
      });
      w.document.open();
      w.document.write(data);
      w.document.close();
    } catch (_e) {
      try { w.close(); } catch { /* noop */ }
      toast.error("Could not open print view");
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    const { lv, sn, pid } = params();
    if (!lv) { toast.error("Pick at least one level"); return; }
    setBusy(true);
    try {
      const res = await api.get(`/assessment-checklist/pdf?levels=${lv}${sn}${pid}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `assessment-${lv}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (_e) {
      toast.error("Could not download PDF");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="print-checklist-modal">
      <div className="bg-[#0A0A0A] border border-[#262626] w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-[#262626]">
          <div>
            <div className="label-mono mb-1" style={{ color: "#CCFF00" }}>
              <FileText className="w-3.5 h-3.5 inline mr-1" /> ASSESSMENT CHECKLIST
            </div>
            <h2 className="h-display text-2xl tracking-tight">Print on-court checklist.</h2>
          </div>
          <button onClick={onClose} className="p-1 text-[#A3A3A3] hover:text-white" data-testid="print-checklist-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="text-xs text-[#A3A3A3]">
            Pick the DUPR levels you want on the printed page. Useful for a fresh first-assessment — take it on court,
            tick off skills with a pen, then come back and key it in.
          </div>

          <div className="flex items-center gap-2 mb-2">
            <button onClick={all} className="text-[10px] uppercase tracking-wider border border-[#262626] hover:border-[#CCFF00] hover:text-[#CCFF00] px-2 py-1" data-testid="print-pick-all">
              Select all
            </button>
            <button onClick={none} className="text-[10px] uppercase tracking-wider border border-[#262626] hover:border-[#FF4500] hover:text-[#FF4500] px-2 py-1" data-testid="print-pick-none">
              Clear
            </button>
            <div className="text-[10px] text-[#525252] ml-auto">{picked.size} of {ALL_LEVELS.length} selected</div>
          </div>

          <div className="grid grid-cols-1 gap-2">
            {ALL_LEVELS.map((lv) => {
              const on = picked.has(lv);
              return (
                <button
                  key={lv}
                  type="button"
                  onClick={() => toggle(lv)}
                  data-testid={`print-level-${lv}`}
                  className="flex items-center gap-3 border p-3 text-left transition-all"
                  style={on
                    ? { borderColor: "#CCFF00", background: "rgba(204,255,0,0.08)" }
                    : { borderColor: "#262626" }}
                >
                  <div
                    className="w-4 h-4 border-2 flex items-center justify-center shrink-0"
                    style={on
                      ? { borderColor: "#CCFF00", background: "#CCFF00" }
                      : { borderColor: "#525252" }}
                  >
                    {on && <div className="w-2 h-2 bg-[#0A0A0A]" />}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-sm">{lv} · {LEVEL_LABELS[lv]}</div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-[#262626]">
            <button
              onClick={openHtml}
              disabled={busy || picked.size === 0}
              className="flex-1 btn-volt px-5 py-3 text-xs inline-flex items-center justify-center gap-2 disabled:opacity-30"
              data-testid="print-open-html"
            >
              <Printer className="w-4 h-4" /> Open print view
            </button>
            <button
              onClick={downloadPdf}
              disabled={busy || picked.size === 0}
              className="flex-1 px-5 py-3 text-xs uppercase tracking-wider border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-[#0A0A0A] inline-flex items-center justify-center gap-2 disabled:opacity-30"
              data-testid="print-download-pdf"
            >
              <Download className="w-4 h-4" /> Download PDF
            </button>
          </div>
          <div className="text-[10px] text-[#525252]">
            Tip: in the print view you can <span className="text-[#A3A3A3]">save as PDF</span> from your browser&apos;s print dialog
            — either way works.
          </div>
        </div>
      </div>
    </div>
  );
}
